// 合圖（sprite sheet）載入 — 把數百筆散圖請求收斂成每組 1 筆。
// 合圖由 `scripts/pack-atlases.mjs` 產生至 `public/images/atlas/<group>.{webp,json}`。
//
// ── 為什麼可以「幾乎零改動」接上 ────────────────────────────────────────────────────
// 合圖的 frame key 就是原本的 URL 路徑（例 `/images/ui/logo.webp`），而 PixiJS 的
// spritesheetAsset 在 parse 完後會把**每個 frame 以 frame key 寫進 `Assets.cache`**
// （見 pixi.js/lib/spritesheet/spritesheetAsset 的 `getCacheableAssets`）。
// 所以各 loader 只要把 `Assets.load(url)` 換成本檔的 `loadTexture(url)`——先查 cache、
// 沒有才真的發請求——就能在「有合圖用合圖、缺合圖退回單張」之間自動切換。
//
// ⚠️ `Assets.load()` 本身**不會**查 `Assets.cache`（只有 loader 內部的 promiseCache），
// 所以這層 cache 查詢必須自己做，不能指望 `Assets.load` 幫忙命中合圖。
//
// ⚠️ 合圖是「可選加速層」：`ensureAtlases()` 失敗只記警告不拋錯，讓遊戲仍能以散圖啟動。
// 代價是合圖過期時不會報錯而是靜默退回散圖——新增/修改 `public/images` 下的圖後，
// 記得重跑 `npm run atlas:pack`。

import { Assets, type Texture } from 'pixi.js'
import { logger } from '../core/Logger'

const ATLAS_BASE = '/images/atlas'

/** group → 載入結果（true=成功、false=失敗已退回散圖）。同一組只載一次。 */
const loading = new Map<string, Promise<boolean>>()

async function loadOne(group: string): Promise<boolean> {
  try {
    await Assets.load(`${ATLAS_BASE}/${group}.json`)
    return true
  } catch (error) {
    logger.warn(`合圖 ${group} 載入失敗，改以散圖載入（請檢查是否已跑過 npm run atlas:pack）`, error)
    return false
  }
}

/**
 * 確保指定合圖已載入。可重複呼叫（同組只發一次請求）。
 * 失敗不拋錯——呼叫端後續的 `loadTexture()` 會自動退回單張檔案。
 */
export async function ensureAtlases(...groups: readonly string[]): Promise<void> {
  await Promise.all(
    groups.map((group) => {
      const existing = loading.get(group)
      if (existing !== undefined) return existing
      const job = loadOne(group)
      loading.set(group, job)
      return job
    }),
  )
}

/**
 * 取貼圖：已在合圖（或已載入過）就直接用快取的子貼圖（0 筆請求），否則退回載入單張檔案。
 * @param url 原始貼圖路徑，與合圖的 frame key 相同。
 */
export async function loadTexture(url: string): Promise<Texture> {
  if (Assets.cache.has(url)) return Assets.cache.get<Texture>(url)
  return Assets.load<Texture>(url)
}

/** 是否已有此貼圖可直接取用（合圖內或先前已載入）。 */
export function hasTexture(url: string): boolean {
  return Assets.cache.has(url)
}
